import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faExclamationTriangle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { consumeTaskThreadDraft } from "@/components/ops/agent-review/taskThreadDraftStorage";
import type {
  MessageAttachment,
  MissionContextData,
  TaskActionSpec,
  TaskDetail,
  TaskEmbedParams,
  ThreadInteraction,
  ThreadMessage,
} from "@/services/api/agentic-v2";
import { useTaskThread } from "@/services/api/agentic-v2/queries/useTaskThread";
import { motion } from "framer-motion";
import { AgentMessageHtml } from "../AgentMessageHtml";
import { OsLink } from "@/components/ui/os-layout/OsLink";
import { LazyEmbed } from "./embeds";
import { LazyToolCallStepDetail } from "./LazyToolCallStepDetail";
import { ThreadArtifactCard } from "./ThreadArtifactCard";
import { prettifyToolName } from "./prettifyToolName";
import { ThreadTimestamp } from "./ThreadTimestamp";
import {
  collapseThreadTimestamps,
  threadItemTimestampIso,
  type ThreadTimestampDisplay,
} from "./threadTimestampUtils";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { useCanAccessGuardians } from "@/hooks/useCanAccessGuardians";
import { Badge, colors } from "@/components/ui/badge/Badge";
import { getAgenticTaskStatusBadgeColor } from "@/components/missions/agenticTaskStatusBadge";
import { Button } from "@/components/ui/button/Button";
import { AnimatedEllipsis } from "@/components/ui/AnimatedEllipsis";
import AgentWorkerAvatar from "@/components/worker/AgentWorkerAvatar";
import { ChatInput } from "@/components/chat/ChatInput";
import { OutboundMessageQueuePanel } from "@/components/chat/OutboundMessageQueuePanel";
import { useOutboundMessageQueue } from "@/components/chat/useOutboundMessageQueue";
import { TaskHumanAssign } from "./TaskHumanAssign";
import { TaskThreadOpsMenuSlot } from "./TaskThreadOpsMenuSlot";
import { taskNeedsCatalogueReviewWorkspace } from "@/components/review/isReviewDeckArtefact";
import { useOpenTaskReview } from "@/components/review/useOpenTaskReview";
import { MissionControlLoadingBar } from "@/components/mission-control/MissionControlLoading";
import {
  TASK_THREAD_ACTION_DESTRUCTIVE_CLASS,
  TASK_THREAD_ACTION_NEUTRAL_CLASS,
  TASK_THREAD_ACTION_PRIMARY_CLASS,
  TASK_THREAD_ACTION_SECONDARY_CLASS,
  TASK_THREAD_ACTION_SNOOZE_ACTIVE_CLASS,
  TASK_THREAD_AGENT_BUBBLE_CLASS,
  TASK_THREAD_BANNER_CLASS,
  TASK_THREAD_BANNER_CODE_CLASS,
  TASK_THREAD_COMPOSE_INPUT_CLASS,
  TASK_THREAD_FIELD_CLASS,
  TASK_THREAD_FRAME_DRAWER_CLASS,
  TASK_THREAD_LABEL_CLASS,
  TASK_THREAD_META_CLASS,
  TASK_THREAD_META_EMPHASIS_CLASS,
  TASK_THREAD_SUMMARY_CLASS,
  TASK_THREAD_TITLE_CLASS,
  TASK_THREAD_USER_BUBBLE_CLASS,
} from "./taskThreadSurface";

/** If the user is within this many px of the bottom, keep pinning on thread updates. */
const TASK_THREAD_BOTTOM_PIN_PX = 120;

const THREAD_ROW_MOTION = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
};

function ThreadRowMotionShell({
  children,
  motionEnabled,
}: {
  children: ReactNode;
  motionEnabled: boolean;
}) {
  return (
    <motion.div
      className="min-w-0"
      initial={motionEnabled ? THREAD_ROW_MOTION.initial : false}
      animate={THREAD_ROW_MOTION.animate}
      transition={THREAD_ROW_MOTION.transition}
    >
      {children}
    </motion.div>
  );
}

/**
 * Shared Task Thread view. Used standalone (Mission Control side panel) or
 * embedded inside Mission Detail's right-hand drawer. Owns its own data
 * fetching given a ``taskId`` so it stays self-contained.
 *
 * Reads from two endpoints:
 *   - GET /tasks/{id}            → header (name, status, agent, actions, DoD)
 *   - GET /tasks/{id}/messages   → real messages + interactions for the thread
 */
export interface TaskThreadProps {
  taskId: string;
  /** Optional close callback (renders an X button when provided). */
  onClose?: () => void;
  /** Optional callback when the task status changes (e.g. via an action). */
  onTaskUpdated?: (task: TaskDetail) => void;
  /** Click handler for the parent mission link in the header. */
  onOpenMission?: (missionId: string) => void;
  /** Called after ops hard-deletes this task. */
  onTaskDeleted?: () => void;
  /** Surface variant — affects padding/borders only. */
  variant?: "drawer" | "panel";
  /** Read-only embed (e.g. Guardians hover preview): hide compose and actions. */
  readOnly?: boolean;
}

/** Task detail drawer/panel — thread, actions, and lazy embeds backed by ``useTaskThread``. */
export function TaskThread({
  taskId,
  onClose,
  onTaskUpdated,
  onOpenMission,
  onTaskDeleted,
  variant = "drawer",
  readOnly = false,
}: TaskThreadProps) {
  const {
    task,
    thread,
    missionContext,
    loading,
    refreshing,
    error,
    isAgentWorking,
    activityLabel,
    setTask,
    setError,
    reloadThread,
  } = useTaskThread({ taskId });

  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeDraft] = useState(() => consumeTaskThreadDraft(taskId) ?? undefined);

  const sendMessageNowRef = useRef<
    (text: string, attachments: MessageAttachment[]) => Promise<void>
  >(async () => {});

  const {
    queue: outboundQueue,
    enqueue: enqueueOutbound,
    removeFromQueue,
  } = useOutboundMessageQueue<MessageAttachment>({
    isAgentBusy: isAgentWorking,
    onSend: (text, attachments) =>
      sendMessageNowRef.current(text, attachments),
  });

  const agentDisplayName = task?.agent?.name ?? "Agent";

  const handleAction = async (
    action: string,
    note?: string,
    label?: string,
    days?: number,
  ) => {
    setBusyAction(action);
    try {
      const updated = await AgenticV2API.applyTaskAction(taskId, {
        action,
        ...(note ? { note } : {}),
        ...(label ? { label } : {}),
        ...(days !== undefined ? { days } : {}),
      });
      setTask(updated);
      onTaskUpdated?.(updated);
      try {
        await reloadThread();
      } catch {
        // non-fatal: header is updated, thread reload can fail silently
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusyAction(null);
    }
  };

  const sendMessageNow = async (
    text: string,
    attachments: MessageAttachment[] = [],
  ) => {
    const trimmed = text.trim();
    if (!trimmed || composeBusy) return;
    setComposeBusy(true);
    try {
      await AgenticV2API.postTaskMessage(taskId, {
        text: trimmed,
        attachments,
      });
      await reloadThread();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setComposeBusy(false);
    }
  };

  sendMessageNowRef.current = sendMessageNow;

  const submitMessage = async (
    text: string,
    attachments: MessageAttachment[] = [],
  ) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (isAgentWorking || composeBusy) {
      enqueueOutbound(trimmed, attachments);
      return;
    }
    await sendMessageNow(trimmed, attachments);
  };

  const followUpPlaceholder = isAgentWorking
    ? `Queue a follow-up — it will send after ${agentDisplayName} finishes the current reply`
    : undefined;

  const items = useMemo(
    () => buildThreadItems(thread?.messages ?? [], thread?.interactions ?? []),
    [thread]
  );
  const timestampDisplays = useMemo(
    () => collapseThreadTimestamps(items.map(threadItemTimestampIso)),
    [items]
  );
  // ── Scroll management (following Chat.tsx pattern) ──────────────────────
  const threadScrollAreaRef = useRef<HTMLDivElement>(null);
  const autoScrollPinnedRef = useRef(true);
  const previousItemsMetaRef = useRef<{ count: number; lastKey: string | null }>({
    count: 0,
    lastKey: null,
  });
  const initialLoadCompleteRef = useRef(false);
  const currentTaskIdRef = useRef<string | null>(null);
  const [threadRowMotionEnabled, setThreadRowMotionEnabled] = useState(false);
  const canAccessGuardians = useCanAccessGuardians();
  const openTaskReview = useOpenTaskReview();

  const isNearBottom = useCallback((el: HTMLDivElement) => {
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= TASK_THREAD_BOTTOM_PIN_PX;
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const el = threadScrollAreaRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    },
    []
  );

  const handleScroll = useCallback(() => {
    const el = threadScrollAreaRef.current;
    if (!el) return;
    autoScrollPinnedRef.current = isNearBottom(el);
  }, [isNearBottom]);

  // Reset on task change
  useLayoutEffect(() => {
    if (currentTaskIdRef.current !== taskId) {
      currentTaskIdRef.current = taskId;
      initialLoadCompleteRef.current = false;
      autoScrollPinnedRef.current = true;
      previousItemsMetaRef.current = { count: 0, lastKey: null };
      setThreadRowMotionEnabled(false);
    }
  }, [taskId]);

  // Scroll to bottom on initial load and when new messages arrive
  useLayoutEffect(() => {
    if (loading || !task || task._id !== taskId) return;

    const el = threadScrollAreaRef.current;
    if (!el) return;

    const lastKey = items[items.length - 1]?.key ?? null;
    const prevMeta = previousItemsMetaRef.current;
    const hasNewItems =
      items.length > prevMeta.count || lastKey !== prevMeta.lastKey;

    previousItemsMetaRef.current = { count: items.length, lastKey };

    // First load for this task: instant scroll, no animation
    if (!initialLoadCompleteRef.current) {
      initialLoadCompleteRef.current = true;
      const pinInstant = () => {
        el.scrollTop = el.scrollHeight;
      };
      pinInstant();
      requestAnimationFrame(() => {
        pinInstant();
        requestAnimationFrame(() => {
          pinInstant();
          // Enable motion for future items after initial paint
          setThreadRowMotionEnabled(true);
        });
      });
      return;
    }

    // New items arrived: smooth scroll if we're following
    if (hasNewItems && autoScrollPinnedRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom("smooth");
      });
    }
  }, [taskId, task, items, loading, scrollToBottom]);

  // Scroll when typing indicator appears (agent starts working)
  useLayoutEffect(() => {
    if (!isAgentWorking || loading || !initialLoadCompleteRef.current) return;
    if (!autoScrollPinnedRef.current) return;

    requestAnimationFrame(() => {
      scrollToBottom("smooth");
    });
  }, [isAgentWorking, loading, scrollToBottom]);

  if (loading && !task) {
    return (
      <Frame variant={variant}>
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
          Loading…
        </div>
      </Frame>
    );
  }

  if ((error || !task) && !refreshing) {
    return (
      <Frame variant={variant}>
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500" />
          {error?.message ?? "Task not found"}
        </div>
      </Frame>
    );
  }

  if (!task) {
    return (
      <Frame variant={variant}>
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
          Loading…
        </div>
      </Frame>
    );
  }

  // Show actions whenever any are present. Built-ins like ``reopen`` are
  // injected by the backend on terminal states, so we no longer gate this
  // on status — let the backend decide which actions apply.
  const showActions = !readOnly && (task.actions?.length ?? 0) > 0;
  const isStale = task._id !== taskId;
  const showGuidedReviewEntry = !readOnly && taskNeedsCatalogueReviewWorkspace(task);

  return (
    <Frame
      variant={variant}
      className={clsx(
        "relative transition-opacity duration-200",
        (refreshing || isStale) && "opacity-60",
      )}
    >
      <MissionControlLoadingBar active={refreshing || isStale} />
      <Header
        task={task}
        onClose={onClose}
        onOpenMission={onOpenMission}
        onTaskDeleted={onTaskDeleted}
        onAssigned={(updated) => {
          setTask(updated);
          onTaskUpdated?.(updated);
        }}
      />

      {showGuidedReviewEntry ? (
        <div
          className="mx-4 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-200/80 bg-indigo-50/90 px-3 py-2 dark:border-indigo-500/30 dark:bg-indigo-500/10"
          data-testid="task-thread-guided-review-entry"
        >
          <p className="min-w-0 text-sm text-slate-700 dark:text-slate-200">
            This task has a guided review — open the review workspace to work through the steps.
          </p>
          <Button
            type="button"
            color="indigo"
            size="sm"
            className="shrink-0"
            data-testid="task-thread-open-review"
            onClick={() => openTaskReview(task._id, task.name, { mode: "review" })}
          >
            Open review
          </Button>
        </div>
      ) : null}

      {task.status === "auto_closed" && (
        <AutoClosedBanner
          display={task.auto_closed_display}
          reason={task.auto_closed_reason}
        />
      )}

      <div
        ref={threadScrollAreaRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3"
      >
        {items.length === 0 ? (
          <p className="text-xs text-slate-500 text-center pt-8">
            No activity yet.
          </p>
        ) : (
          items.map((item, index) => (
            <ThreadRowMotionShell
              key={item.key}
              motionEnabled={threadRowMotionEnabled}
            >
              <MemoThreadItemRow
                item={item}
                missionContext={missionContext}
                timestampDisplay={timestampDisplays[index] ?? null}
                canAccessGuardians={canAccessGuardians}
              />
            </ThreadRowMotionShell>
          ))
        )}

        {/* Typing indicator when agent is working */}
        {isAgentWorking && (
          <p
            className="mt-4 text-xs text-slate-400 font-medium"
            data-testid="task-thread-typing-indicator"
            aria-live="polite"
            aria-busy="true"
          >
            {activityLabel
              ? `${task.agent?.name ?? "Agent"} · ${activityLabel.replace(/…$/, "")}`
              : `${task.agent?.name ?? "Agent"} is working`}
            <span aria-hidden className="inline-block">
              <AnimatedEllipsis />
            </span>
          </p>
        )}
      </div>

      {showActions && (
        <ActionBar
          actions={task.actions}
          busyAction={busyAction}
          onAction={handleAction}
        />
      )}

      {!readOnly && !isTerminalTask(task.status) && (
        <div data-testid="task-thread-compose">
          <OutboundMessageQueuePanel
            queue={outboundQueue}
            agentLabel={agentDisplayName}
            onRemove={removeFromQueue}
          />
          <ChatInput
            recipientId={taskId}
            recipientName={agentDisplayName}
            workflowTaskId={taskId}
            onSendMessage={submitMessage}
            disabled={composeBusy}
            placeholder={followUpPlaceholder}
            miniUpload
            initialDraft={composeDraft}
            inputContainerClassName={TASK_THREAD_COMPOSE_INPUT_CLASS}
          />
        </div>
      )}
    </Frame>
  );
}

// ── Thread item construction ──────────────────────────────────────────

/**
 * One renderable row in the thread. Built by interleaving messages with
 * the `tool_call` steps of their parent interaction so the UI surfaces
 * "tools used → response" inline.
 *
 * The internal `thinking` and the final `reasoning` step are intentionally
 * hidden from the user. The user-facing audit fields (``summary``,
 * ``disposition_reasoning``, ``blockers``, ``confidence``) are attached
 * to the agent's final message instead — see ``MessageRow``.
 */
type ThreadItem =
  | {
    kind: "message";
    key: string;
    message: ThreadMessage;
  }
  | {
    kind: "step";
    key: string;
    interactionId: string;
    stepIndex: number;
    step: ThreadInteraction["steps"][number];
  };

/**
 * Build a chronologically-correct timeline by interleaving messages and
 * tool_call steps within each interaction, then concatenating interactions
 * in start order.
 *
 * Within an interaction the runner does (per loop iteration):
 *   1. agent decides → may emit an interim message
 *   2. tool runs → step appended to interaction.steps
 *   3. (next iteration, or finish)
 *
 * To recover the visual order we merge each interaction's messages and
 * its non-reasoning steps by timestamp. Reasoning steps are dev-only and
 * hidden (the user-facing parts live on the final message itself).
 *
 * Messages that have no `interaction_id` (e.g. system events, user
 * sends) are placed in time order alongside the interaction-grouped
 * rows.
 */
function buildThreadItems(
  messages: ThreadMessage[],
  interactions: ThreadInteraction[]
): ThreadItem[] {
  const byInteraction = new Map<string, ThreadMessage[]>();
  const orphanMessages: ThreadMessage[] = [];

  for (const m of messages) {
    if (m.interaction_id) {
      const bucket = byInteraction.get(m.interaction_id);
      if (bucket) bucket.push(m);
      else byInteraction.set(m.interaction_id, [m]);
    } else {
      orphanMessages.push(m);
    }
  }

  type Entry = { ts: number; item: ThreadItem };
  const entries: Entry[] = [];

  // Each interaction contributes its own merged timeline.
  for (const interaction of interactions) {
    const interactionMessages = byInteraction.get(interaction._id) ?? [];
    const interactionStart = new Date(interaction.started_at).getTime();
    const localEntries: Entry[] = [];

    interactionMessages.forEach((m) => {
      localEntries.push({
        ts: new Date(m.created_at).getTime(),
        item: { kind: "message", key: m._id, message: m },
      });
    });

    interaction.steps.forEach((step, i) => {
      // Reasoning steps are internal — never shown.
      if (step.type === "reasoning") return;
      const ts = step.timestamp
        ? new Date(step.timestamp).getTime()
        : interactionStart + i;
      localEntries.push({
        ts,
        item: {
          kind: "step",
          key: `${interaction._id}:step:${i}`,
          interactionId: interaction._id,
          stepIndex: i,
          step,
        },
      });
    });

    localEntries.sort((a, b) => a.ts - b.ts);
    entries.push(...localEntries);
  }

  // Orphan messages (system events, user posts) slot in by their own ts.
  for (const m of orphanMessages) {
    entries.push({
      ts: new Date(m.created_at).getTime(),
      item: { kind: "message", key: m._id, message: m },
    });
  }

  entries.sort((a, b) => a.ts - b.ts);
  return entries.map((e) => e.item);
}

function ThreadItemRow({
  item,
  missionContext,
  timestampDisplay,
  canAccessGuardians,
}: {
  item: ThreadItem;
  missionContext?: MissionContextData | null;
  timestampDisplay: ThreadTimestampDisplay | null;
  canAccessGuardians: boolean;
}) {
  if (item.kind === "step") {
    return (
      <MemoStepRow
        interactionId={item.interactionId}
        stepIndex={item.stepIndex}
        step={item.step}
        timestampDisplay={timestampDisplay}
        canAccessGuardians={canAccessGuardians}
      />
    );
  }
  return (
    <MemoMessageRow
      message={item.message}
      missionContext={missionContext}
      timestampDisplay={timestampDisplay}
    />
  );
}

// Memoised rows — parent holds selection/scroll state that changes often.
const MemoThreadItemRow = memo(ThreadItemRow);

function StepRow({
  interactionId,
  stepIndex,
  step,
  timestampDisplay,
  canAccessGuardians,
}: {
  interactionId: string;
  stepIndex: number;
  step: ThreadInteraction["steps"][number];
  timestampDisplay: ThreadTimestampDisplay | null;
  canAccessGuardians: boolean;
}) {
  if (step.type === "tool_call") {
    const label = step.tool_display_name || prettifyToolName(step.tool);
    const toolName = canAccessGuardians ? (
      <Tooltip
        placement="top"
        maxWidth="min(24rem, 92vw)"
        content={
          <LazyToolCallStepDetail
            interactionId={interactionId}
            stepIndex={stepIndex}
          />
        }
      >
        <span className="text-slate-600 underline decoration-dotted decoration-slate-400 underline-offset-2 cursor-help dark:text-slate-300 dark:decoration-slate-600">
          {label}
        </span>
      </Tooltip>
    ) : (
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
    );
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
        <FontAwesomeIcon icon={faCircleCheck} className="text-[10px] shrink-0" />
        <span className="min-w-0">Used {toolName}</span>
        {timestampDisplay && (
          <ThreadTimestamp
            display={timestampDisplay}
            className="ml-auto shrink-0 text-slate-500"
          />
        )}
      </div>
    );
  }
  // Other (non-reasoning, non-tool_call) step types: show as muted row.
  return (
    <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
      <FontAwesomeIcon icon={faClock} className="text-[10px] shrink-0" />
      <span className="min-w-0">{step.content ?? step.type}</span>
      {timestampDisplay && (
        <ThreadTimestamp
          display={timestampDisplay}
          className="ml-auto shrink-0 text-slate-500"
        />
      )}
    </div>
  );
}

const MemoStepRow = memo(StepRow);

function MessageRow({
  message,
  missionContext,
  timestampDisplay,
}: {
  message: ThreadMessage;
  missionContext?: MissionContextData | null;
  timestampDisplay: ThreadTimestampDisplay | null;
}) {
  if (message.sender_type === "system" || message.content_type === "system_event") {
    const sysContent = message.content as { label?: string; note?: string };
    const label = sysContent.label ?? "System event";
    return (
      <div className="text-xs text-slate-400 py-1">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={iconForLabel(label)} className="text-[10px]" />
          <span>{label}</span>
          {timestampDisplay && (
            <ThreadTimestamp
              display={timestampDisplay}
              className="ml-auto text-slate-500"
            />
          )}
        </div>
        {sysContent.note && (
          <p className="mt-0.5 pl-4 italic text-slate-600 dark:text-slate-300">
            “{sysContent.note}”
          </p>
        )}
      </div>
    );
  }

  if (message.sender_type === "user") {
    const userContent = message.content as {
      text?: string;
      attachments?: Array<{ display_name?: string; document_id?: string }>;
    };
    const text = userContent.text ?? "";
    const attachments = userContent.attachments ?? [];
    let renderedContent: React.ReactNode = <p className="leading-relaxed">{text}</p>;

    if (text.startsWith("info_request_response:")) {
      try {
        const jsonPart = text.substring("info_request_response:".length).trim();
        const payload = JSON.parse(jsonPart) as Record<
          string,
          {
            severity: string;
            label: string;
            answer?: string;
            skipped?: boolean;
            skip_reason?: string;
          }
        >;
        const entries = Object.values(payload);
        if (entries.length > 0) {
          if (entries.length === 1) {
            const entry = entries[0];
            if (entry.skipped) {
              renderedContent = (
                <p className="leading-relaxed italic text-slate-600 dark:text-slate-300">
                  Skipped{entry.skip_reason ? `: ${entry.skip_reason}` : ""}
                </p>
              );
            } else {
              renderedContent = <p className="leading-relaxed">{entry.answer}</p>;
            }
          } else {
            renderedContent = (
              <div className="space-y-2.5">
                {entries.map((entry, idx) => (
                  <div key={idx} className="space-y-0.5">
                    <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
                      {entry.label}
                    </p>
                    {entry.skipped ? (
                      <p className="leading-relaxed italic text-slate-600 dark:text-slate-300">
                        Skipped{entry.skip_reason ? `: ${entry.skip_reason}` : ""}
                      </p>
                    ) : (
                      <p className="leading-relaxed">{entry.answer}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          }
        }
      } catch {
        renderedContent = <p className="leading-relaxed">{text}</p>;
      }
    }

    return (
      <div className="flex justify-end">
        <div className={TASK_THREAD_USER_BUBBLE_CLASS}>
          {renderedContent}
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
              {attachments.map((att, idx) => (
                <li key={`${att.document_id ?? "att"}-${idx}`}>
                  📎 {att.display_name ?? att.document_id ?? "Attachment"}
                </li>
              ))}
            </ul>
          )}
          {timestampDisplay && (
            <p className="text-[10px] text-slate-400 mt-1 text-right">
              <ThreadTimestamp display={timestampDisplay} />
            </p>
          )}
        </div>
      </div>
    );
  }

  if (message.content_type === "artifact_card") {
    return (
      <ThreadArtifactCard
        message={message}
        timestampDisplay={timestampDisplay}
      />
    );
  }

  if (message.content_type === "embed_card") {
    const content = message.content as {
      kind?: string;
      label?: string | null;
      params?: TaskEmbedParams;
    };
    if (!content.kind) {
      return (
        <div className="px-3 py-2 text-xs italic text-slate-500">
          Unsupported embed
        </div>
      );
    }
    return (
      <div>
        <LazyEmbed
          kind={content.kind}
          params={content.params ?? {}}
          label={content.label ?? null}
          taskId={message.task_id ?? undefined}
          missionContext={missionContext}
        />
        {timestampDisplay && (
          <p className="text-[10px] text-slate-500 mt-1 text-right">
            <ThreadTimestamp display={timestampDisplay} />
          </p>
        )}
      </div>
    );
  }

  // Agent text (default).
  const content = message.content as {
    text?: string;
    agent_name?: string | null;
    agent_colour?: string | null;
  };
  const text = content.text ?? "";
  return (
    <div className={TASK_THREAD_AGENT_BUBBLE_CLASS}>
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1.5">
          <AgentWorkerAvatar
            name={content.agent_name ?? "Agent"}
            avatarColour={content.agent_colour}
            size={18}
            className="flex-shrink-0"
          />
          <span className="font-medium text-slate-600 dark:text-slate-300">
            {content.agent_name ?? "Agent"}
          </span>
        </div>
        {timestampDisplay && (
          <ThreadTimestamp display={timestampDisplay} className="text-slate-500 dark:text-slate-400" />
        )}
      </div>
      <AgentMessageHtml html={text} />
    </div>
  );
}

const MemoMessageRow = memo(MessageRow);


// ── Frame ──────────────────────────────────────────────────────────────

function Frame({
  children,
  variant,
  className,
}: {
  children: React.ReactNode;
  variant: "drawer" | "panel";
  className?: string;
}) {
  return (
    <div
      data-testid="task-thread"
      className={clsx(
        "flex flex-col h-full overflow-hidden",
        variant === "drawer"
          ? TASK_THREAD_FRAME_DRAWER_CLASS
          : "bg-transparent",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────────

function Header({
  task,
  onClose,
  onOpenMission,
  onTaskDeleted,
  onAssigned,
}: {
  task: TaskDetail;
  onClose?: () => void;
  onOpenMission?: (missionId: string) => void;
  onTaskDeleted?: () => void;
  onAssigned: (task: TaskDetail) => void;
}) {
  return (
    <header className="flex items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className={TASK_THREAD_LABEL_CLASS}>
            Task
          </span>
          <Badge color={getAgenticTaskStatusBadgeColor(task.status)} size="sm">
            {task.status.replace(/_/g, " ")}
          </Badge>
          {task.priority !== "normal" && (
            <Badge color={taskThreadPriorityColor(task.priority)} size="sm">
              {task.priority}
            </Badge>
          )}
          <TaskHumanAssign task={task} onAssigned={onAssigned} />
        </div>
        <h2 className={TASK_THREAD_TITLE_CLASS}>
          {task.name}
        </h2>
        <div className={clsx(TASK_THREAD_META_CLASS, "mt-2 flex flex-wrap items-center gap-1.5")}>
          {task.agent && (
            <>
              <AgentWorkerAvatar
                name={task.agent.name}
                avatarColour={task.agent.avatar_colour}
                size={20}
                className="flex-shrink-0"
              />
              <span className={TASK_THREAD_META_EMPHASIS_CLASS}>{task.agent.name}</span>
              <span>·</span>
            </>
          )}
          {task.mission?.id && task.mission?.name && (
            <>
              <button
                type="button"
                onClick={() => onOpenMission?.(task.mission!.id!)}
                className={clsx(
                  TASK_THREAD_META_EMPHASIS_CLASS,
                  "max-w-[14rem] truncate hover:text-slate-900 hover:underline dark:hover:text-slate-100",
                )}
              >
                {task.mission.name}
              </button>
              <span>·</span>
            </>
          )}
          {task.entity?.display_name && (
            <span className={clsx(TASK_THREAD_META_EMPHASIS_CLASS, "truncate")}>
              {task.entity.display_name}
            </span>
          )}
        </div>
        {task.latest_summary && (
          <p className={clsx(TASK_THREAD_SUMMARY_CLASS, "mt-1.5")}>
            {task.latest_summary}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <TaskThreadOpsMenuSlot
          taskId={task._id}
          taskName={task.name}
          taskStatus={task.status}
          missionId={task.mission?.id ?? null}
          onDeleted={onTaskDeleted}
        />
        {onClose ? (
          <Button
            type="button"
            color="color"
            data-testid="task-thread-close"
            onClick={onClose}
            title="Close"
            aria-label="Close task"
            icon={<FontAwesomeIcon icon={faXmark} />}
          >
            {""}
          </Button>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Terminal-good statuses where the task is finished and the composer
 * should be hidden — sending a message on these makes no sense in the
 * current model. (Users can still talk on the parent mission thread.)
 */
function isTerminalTask(status: string): boolean {
  return status === "done" || status === "auto_closed";
}

/**
 * Banner shown above the thread when a task is blocked by a soft gate
 * (system-level — the mission's branching engine declined to start this
 * task because its preconditions weren't met yet).
 *
 * Renders human copy from ``gate_blocked_display`` (snapshotted from
 * the mission template) — never the raw gate expression. The raw
 * ``gate_blocked_reason`` is tucked behind a Details disclosure for
 * power users / debugging.
 *
 * Agent-emitted ``blockers`` (LLM "I need more info") are not banner-ed
 * — they show up in Salty's summary line + her message in the thread.
 */
/**
 * Banner when a task was closed automatically (trigger, mission archive, etc.).
 */
function AutoClosedBanner({
  display,
  reason,
}: {
  display?: string | null;
  reason?: string | null;
}) {
  const body =
    display?.trim() ||
    "This task was closed automatically because it is no longer needed.";

  return (
    <div
      data-testid="auto-closed-banner"
      className={TASK_THREAD_BANNER_CLASS}
    >
      <p className={clsx(TASK_THREAD_LABEL_CLASS, "mb-1")}>
        Auto-closed
      </p>
      <p className={clsx(TASK_THREAD_SUMMARY_CLASS)}>{body}</p>
      {reason?.trim() ? (
        <details className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          <summary className="cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-300">
            Details
          </summary>
          <code className={TASK_THREAD_BANNER_CODE_CLASS}>
            {reason.trim()}
          </code>
        </details>
      ) : null}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function iconForLabel(label: string | null | undefined) {
  if (!label) return faClock;
  const l = label.toLowerCase();
  if (l.includes("complet") || l.includes("done")) return faCircleCheck;
  if (l.includes("block") || l.includes("error")) return faCircleXmark;
  return faClock;
}

/** Fallback when a tool has no catalogue ``display_name``. */
// ── Action bar + compose ──────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  approve: "Approve",
  reject: "Reject",
  request_changes: "Request changes",
  snooze: "Snooze…",
  mark_done: "Mark done",
  reassign: "Reassign",
};

const PRIMARY_ACTIONS = new Set(["approve", "mark_done"]);
const DESTRUCTIVE_ACTIONS = new Set([
  "reject",
  "override_approve",
  "proceed_to_assessment",
]);
const SECONDARY_ACTIONS = new Set(["reopen"]);

/** Day chips offered by the inline Snooze picker. */
const SNOOZE_DAY_PRESETS = [1, 3, 7, 14, 30] as const;
const DEFAULT_SNOOZE_DAYS = 7;

function ActionBar({
  actions,
  busyAction,
  onAction,
}: {
  actions: (TaskActionSpec | string)[];
  busyAction: string | null;
  onAction: (
    action: string,
    note?: string,
    label?: string,
    days?: number,
  ) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const rejectNoteRef = useRef<HTMLTextAreaElement>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeDays, setSnoozeDays] = useState<number>(DEFAULT_SNOOZE_DAYS);

  useLayoutEffect(() => {
    if (rejectOpen) rejectNoteRef.current?.focus();
  }, [rejectOpen]);

  if (rejectOpen) {
    const send = () => {
      const note = rejectNote.trim() || undefined;
      onAction("reject", note);
      setRejectOpen(false);
      setRejectNote("");
    };
    return (
      <div
        data-testid="task-thread-reject-composer"
        className="px-3 py-2 space-y-2"
      >
        <textarea
          ref={rejectNoteRef}
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          placeholder="What's wrong? (optional — leave blank to just reject)"
          rows={2}
          className={clsx(TASK_THREAD_FIELD_CLASS, "focus:ring-red-500/30")}
        />
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => {
              setRejectOpen(false);
              setRejectNote("");
            }}
            className={clsx("rounded-lg px-3 py-1.5 text-xs font-medium", TASK_THREAD_ACTION_NEUTRAL_CLASS)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={send}
            className={clsx("rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50", TASK_THREAD_ACTION_DESTRUCTIVE_CLASS)}
          >
            {rejectNote.trim() ? "Send & re-run" : "Reject"}
          </button>
        </div>
      </div>
    );
  }

  if (snoozeOpen) {
    const send = () => {
      const days =
        Number.isFinite(snoozeDays) && snoozeDays >= 1 && snoozeDays <= 365
          ? Math.round(snoozeDays)
          : DEFAULT_SNOOZE_DAYS;
      const label = days === 1 ? "Snoozed 1 day" : `Snoozed ${days} days`;
      onAction("snooze", undefined, label, days);
      setSnoozeOpen(false);
    };
    return (
      <div
        data-testid="task-thread-snooze-picker"
        className="px-3 py-2 space-y-2"
      >
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-slate-400 mr-1">Snooze for</span>
          {SNOOZE_DAY_PRESETS.map((d) => {
            const active = d === snoozeDays;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setSnoozeDays(d)}
                className={clsx(
                  "px-2 py-1 rounded-md text-xs font-medium transition-colors",
                  active
                    ? TASK_THREAD_ACTION_SNOOZE_ACTIVE_CLASS
                    : TASK_THREAD_ACTION_NEUTRAL_CLASS,
                )}
              >
                {d}d
              </button>
            );
          })}
          <input
            type="number"
            min={1}
            max={365}
            value={snoozeDays}
            onChange={(e) => setSnoozeDays(Number(e.target.value))}
            className={clsx(TASK_THREAD_FIELD_CLASS, "w-16 rounded-md px-2 py-1 focus:ring-indigo-500/30")}
            aria-label="Custom snooze days"
          />
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => {
              setSnoozeOpen(false);
              setSnoozeDays(DEFAULT_SNOOZE_DAYS);
            }}
            className={clsx("rounded-lg px-3 py-1.5 text-xs font-medium", TASK_THREAD_ACTION_NEUTRAL_CLASS)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={send}
            className={clsx("rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50", TASK_THREAD_ACTION_SECONDARY_CLASS)}
          >
            Snooze
          </button>
        </div>
      </div>
    );
  }

  // Hide the legacy ``snooze_30d`` button when the universal ``snooze`` is
  // also offered — both go through the same gate, the picker covers 30d.
  const hasUniversalSnooze = actions.some(
    (a) => normaliseAction(a).key === "snooze",
  );
  const visibleActions = hasUniversalSnooze
    ? actions.filter((a) => normaliseAction(a).key !== "snooze_30d")
    : actions;

  return (
    <div
      data-testid="task-thread-actions"
      className="px-3 py-2 flex items-center gap-2 flex-wrap"
    >
      {visibleActions.map((entry) => {
        const { key, label, type, url, confirm } = normaliseAction(entry);
        const isPrimary = PRIMARY_ACTIONS.has(key);
        const isDestructive = DESTRUCTIVE_ACTIONS.has(key);
        const isSecondary = SECONDARY_ACTIONS.has(key);
        const busy = busyAction === key;
        const cls = clsx(
          "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50",
          isPrimary
            ? clsx(TASK_THREAD_ACTION_PRIMARY_CLASS, "flex-1")
            : isDestructive
              ? TASK_THREAD_ACTION_DESTRUCTIVE_CLASS
              : isSecondary
                ? clsx(TASK_THREAD_ACTION_SECONDARY_CLASS, "flex-1")
                : TASK_THREAD_ACTION_NEUTRAL_CLASS
        );

        if (type === "link" && url) {
          // ``OsLink`` opens the target as an OS window in OS mode (deep
          // links into the right app via the URL→window mapping) and
          // falls back to a normal in-app navigation everywhere else.
          return (
            <OsLink
              key={key}
              to={url}
              data-testid={`task-action-${key}`}
              className={clsx(cls, "inline-flex items-center justify-center")}
            >
              {label}
            </OsLink>
          );
        }

        return (
          <button
            key={key}
            type="button"
            disabled={busyAction !== null}
            onClick={() => {
              if (key === "reject") {
                setRejectOpen(true);
                return;
              }
              if (key === "snooze") {
                setSnoozeOpen(true);
                return;
              }
              if (confirm && !window.confirm(confirm)) {
                return;
              }
              onAction(key, undefined, label);
            }}
            data-testid={`task-action-${key}`}
            className={cls}
          >
            {busy ? "…" : label}
          </button>
        );
      })}
    </div>
  );
}

/** Normalise legacy string actions and rich specs to a common shape.
 *
 * For rendering purposes the FE only cares whether the action is a
 * pure link (renders ``OsLink``) or a backend-bound button. The
 * distinction between ``action`` / ``hard_done`` / ``nudge_agent``
 * only matters server-side, so we collapse them all to ``"action"``
 * here.
 */
function normaliseAction(entry: TaskActionSpec | string): {
  key: string;
  label: string;
  type: "action" | "link";
  url: string | null;
  confirm: string | null;
} {
  if (typeof entry === "string") {
    return {
      key: entry,
      label: ACTION_LABELS[entry] ?? entry.replace(/_/g, " "),
      type: "action",
      url: null,
      confirm: null,
    };
  }
  const key = entry.key;
  const label =
    entry.label ?? ACTION_LABELS[key] ?? key.replace(/_/g, " ");
  return {
    key,
    label,
    type: entry.type === "link" ? "link" : "action",
    url: entry.url ?? null,
    confirm: entry.confirm ?? null,
  };
}

// ── Bits & pieces ──────────────────────────────────────────────────────

function taskThreadPriorityColor(priority: string): keyof typeof colors {
  if (priority === "urgent") return "red";
  if (priority === "high") return "amber";
  if (priority === "low") return "zinc";
  return "zinc";
}
